import { useEffect, useRef } from 'react';

import { getApiKey } from '../config/mapyApi';
import { DEFAULT_PANORAMA_TOLERANCE } from '../constants/game';
import { panoramaCache } from '../util/panoramaCache';

/**
 * OPTIMALIZACE: Hook pro pre-loading panoramat v battle módu
 *
 * V battle módu jsou všechna kola (rounds) známa předem z Firebase.
 * Tento hook asynchronně preloaduje panoramata pro všechna kola,
 * takže když hráč přejde na další kolo, panorama je už v cache.
 *
 * @param {Array} rounds - Pole kol z battle, každé obsahuje panoramaPlace
 * @param {boolean} isGameStarted - Zda hra začala
 * @param {boolean} enabled - Zda je preloading povolen (pouze pro battle)
 */
const usePanoramaPreload = (rounds = [], isGameStarted = false, enabled = false) => {
    const preloadedRef = useRef(new Set());
    const isPreloadingRef = useRef(false);

    useEffect(() => {
        if (!enabled || !isGameStarted || !rounds.length) {
            return;
        }

        // Zabránit duplicitnímu preloadingu
        if (isPreloadingRef.current) {
            return;
        }

        const preloadPanoramas = async () => {
            isPreloadingRef.current = true;

            for (const round of rounds) {
                const { panoramaPlace, roundId } = round;

                // Přeskočíme pokud už bylo preloadováno
                if (!panoramaPlace || preloadedRef.current.has(roundId)) {
                    continue;
                }

                const { latitude, longitude } = panoramaPlace;

                // Zkontrolujeme jestli už není v cache
                const cached = panoramaCache.get(latitude, longitude, DEFAULT_PANORAMA_TOLERANCE);
                if (cached) {
                    preloadedRef.current.add(roundId);
                    continue;
                }

                try {
                    // Použijeme panoramaExists pro preload
                    if (window.Panorama) {
                        const result = await window.Panorama.panoramaExists({
                            lon: longitude,
                            lat: latitude,
                            apiKey: getApiKey(),
                            radius: DEFAULT_PANORAMA_TOLERANCE,
                        });

                        if (result?.exists && result.info) {
                            // Uložíme do cache
                            panoramaCache.set(latitude, longitude, DEFAULT_PANORAMA_TOLERANCE, {
                                lat: result.info.lat,
                                lon: result.info.lon,
                                date: result.info.date,
                            });
                            preloadedRef.current.add(roundId);
                        }
                    }

                    // Malé zpoždění mezi requesty aby nezahltilo server
                    await new Promise(resolve => setTimeout(resolve, 100));
                } catch (error) {
                    // Tiše ignorujeme chyby při preloadingu
                    console.debug(`Preload failed for round ${roundId}:`, error);
                }
            }

            isPreloadingRef.current = false;
        };

        // Spustíme preload s malým zpožděním po startu hry
        const timeoutId = setTimeout(preloadPanoramas, 1000);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [enabled, isGameStarted, rounds]);

    // Vrátíme statistiky pro debugging
    return {
        preloadedCount: preloadedRef.current.size,
        totalRounds: rounds.length,
        isPreloading: isPreloadingRef.current,
    };
};

export default usePanoramaPreload;
